import { Component, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ChartConfiguration, ChartDataset } from 'chart.js';
import {
  SALES_INSIGHTS_OTHERS_KEY,
  SalesInsightsBreakdownResponse,
  SalesInsightsBreakdownRow
} from '../../models/sales-insights.model';
import { formatSalesInsightsAmount, formatSalesInsightsCompact } from '../../utils/sales-insights-format';
import { salesInsightsNoValueLabel } from '../../utils/sales-insights-labels';
import {
  salesInsightsSeries,
  salesInsightsRgba,
  salesInsightsThemeColors
} from '../../utils/sales-insights-theme';
import { SalesInsightsBaseComponent } from '../sales-insights-base/sales-insights-base.component';

type DonutMeasure = 'valueBase' | 'valueLocal' | 'quantity' | 'rowCount';

export interface SalesInsightsDonutSlice {
  key: string;
  label: string;
  code: string | null;
  value: number;
  share: string;
  colour: string;
}

const shareFormat = new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 1 });

@Component({
  selector: 'lib-sales-insights-donut',
  standalone: false,
  templateUrl: './sales-insights-donut.component.html',
  styleUrl: './sales-insights-donut.component.scss'
})
export class SalesInsightsDonutComponent extends SalesInsightsBaseComponent<SalesInsightsBreakdownResponse> {
  protected translate = inject(TranslateService);

  slices: SalesInsightsDonutSlice[] = [];
  total = 0;
  totalText = '';
  totalTitle = '';
  unit = '';
  labels: string[] = [];
  datasets: ChartDataset<'doughnut'>[] = [];
  chartOptions: ChartConfiguration<'doughnut'>['options'];

  get measure(): DonutMeasure {
    return this.options.measure || 'valueBase';
  }

  protected defaultSubtitle(): string {
    return this.periodLabelKey();
  }

  protected isEmpty(data: SalesInsightsBreakdownResponse | null): boolean {
    return !(data?.rows || []).some(row => (row[this.measure] ?? 0) > 0);
  }

  protected onData(data: SalesInsightsBreakdownResponse | null): void {
    const groupBy = this.config?.source?.params?.groupBy as string | undefined;
    const rows = this.orderedRows(data?.rows || []);
    const theme = salesInsightsThemeColors();
    const series = salesInsightsSeries();
    const others = salesInsightsRgba(theme.bodyRgb, 0.18);

    this.total = rows.reduce((sum, row) => sum + this.valueOf(row), 0);
    this.unit = this.measure === 'valueBase' ? this.translate.instant('SALES_INSIGHTS_UNIT_BASE_CURRENCY') : '';
    this.totalText = formatSalesInsightsCompact(this.total);
    this.totalTitle = `${formatSalesInsightsAmount(this.total)} ${this.unit}`.trim();
    let step = 0;
    this.slices = rows.map(row => ({
      key: row.key,
      label: this.labelOf(row, groupBy),
      code: row.key && row.name && row.name !== row.key && row.key !== SALES_INSIGHTS_OTHERS_KEY ? row.key : null,
      value: this.valueOf(row),
      share: this.total > 0 ? `${shareFormat.format((this.valueOf(row) / this.total) * 100)} %` : '',
      colour: row.key === SALES_INSIGHTS_OTHERS_KEY ? others : `rgb(${series[step++ % series.length]})`
    }));
    this.labels = this.slices.map(slice => slice.label);
    this.datasets = [
      {
        data: this.slices.map(slice => slice.value),
        backgroundColor: this.slices.map(slice => slice.colour),
        hoverBackgroundColor: this.slices.map(slice => slice.colour),
        borderColor: salesInsightsRgba(theme.surfaceRgb, 1),
        borderWidth: 2,
        hoverOffset: 4
      }
    ];
    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      cutout: '70%',
      layout: { padding: 4 },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: ctx => {
              const slice = this.slices[ctx.dataIndex];
              const label = slice.code ? `${slice.label} (${slice.code})` : slice.label;
              return ` ${label}: ${formatSalesInsightsAmount(slice.value)} ${this.unit} (${slice.share})`;
            }
          }
        }
      }
    };
  }

  private orderedRows(rows: SalesInsightsBreakdownRow[]): SalesInsightsBreakdownRow[] {
    const positive = rows.filter(row => this.valueOf(row) > 0);
    return [
      ...positive
        .filter(row => row.key !== SALES_INSIGHTS_OTHERS_KEY)
        .sort((a, b) => this.valueOf(b) - this.valueOf(a)),
      ...positive.filter(row => row.key === SALES_INSIGHTS_OTHERS_KEY)
    ];
  }

  private valueOf(row: SalesInsightsBreakdownRow): number {
    return Math.max(row[this.measure] ?? 0, 0);
  }

  private labelOf(row: SalesInsightsBreakdownRow, groupBy: string | undefined): string {
    if (row.key === SALES_INSIGHTS_OTHERS_KEY) {
      return this.translate.instant('SALES_INSIGHTS_OTHERS');
    }
    return row.key ? row.name || row.key : salesInsightsNoValueLabel(this.translate, groupBy);
  }
}
